import React, { useState, useMemo } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import {
  INR, today, uuid, notify, notifyCompanyUsers, staffUserIds, clientPayable,
  applyApprovedPayment, resolveInvoiceFinancials,
} from '../../core/helpers';
import { sb } from '../../core/db';
import { Icon, InlineIcon } from '../../components/Icon';
import { runRazorpayPaymentLive } from '../../payments/razorpayLive';

const RZP_MODES = [
  { id: "upi", label: "UPI", icon: "phone" },
  { id: "card", label: "Card", icon: "card" },
  { id: "nb", label: "Net Banking", icon: "bank" },
];

const MANUAL_MODES = [
  { id: "upi", label: "UPI" },
  { id: "bank_transfer", label: "Bank Transfer / NEFT / IMPS" },
  { id: "cheque", label: "Cheque" },
  { id: "cash", label: "Cash" },
];

/** Client pays via Razorpay Checkout, or submits offline payment for team approval. */
function PayNowModal({ data, onClose, invoices, setInvoices, payments, setPayments, clients, showToast }) {
  const { invoiceId, amount: instAmount, scheduleId: dataScheduleId } = data;
  const { users, userLinks, paymentSchedule, org, setClients } = useApp();
  const invoice = invoices.find(i => i.id === invoiceId);
  const client = clients.find(c => c.id === invoice?.clientId);
  const [step, setStep] = useState("form"); // form | submitting | success | failed
  const [errMsg, setErrMsg] = useState("");
  const [flow, setFlow] = useState("razorpay"); // razorpay | manual

  const payable = useMemo(
    () => (invoice ? clientPayable(invoice, paymentSchedule) : { amount: 0, scheduleId: null, hasInstallments: false }),
    [invoice, paymentSchedule],
  );

  const outstanding = useMemo(() => {
    if (!invoice) return 0;
    if (instAmount != null) return Number(instAmount) || 0;
    if (payable.amount > 0) return payable.amount;
    return Math.max(0, Number(invoice.pending) || 0);
  }, [invoice, instAmount, payable.amount]);

  const effectiveScheduleId = dataScheduleId || payable.scheduleId || null;

  const [rzpMethod, setRzpMethod] = useState("upi");
  const [form, setForm] = useState({
    mode: "upi",
    reference: "",
    date: new Date().toISOString().split("T")[0],
    notes: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (!invoice) return null;

  const customer = {
    name: client?.contactName || client?.name || invoice.clientName || "",
    email: client?.email || "",
    phone: client?.phone || "",
  };

  const recordRazorpayPayment = async (rzpResponse, order) => {
    const paidAmt = outstanding;
    const paymentId = uuid();
    const reference = rzpResponse.razorpay_payment_id;
    const notes = `Razorpay · order ${rzpResponse.razorpay_order_id}`;

    await sb.insert("payments", {
      id: paymentId,
      invoice_id: invoiceId,
      company_id: invoice.clientId,
      client_name: invoice.clientName,
      amount: paidAmt,
      mode: "razorpay",
      reference,
      date: today(),
      notes,
      status: "approved",
      schedule_id: effectiveScheduleId || null,
      recorded_by: "client",
    });

    const resolvedInvoice = resolveInvoiceFinancials(invoice, payments);
    const payment = {
      id: paymentId,
      invoiceId,
      clientId: invoice.clientId,
      clientName: invoice.clientName,
      amount: paidAmt,
      mode: "razorpay",
      reference,
      date: today(),
      notes,
      recordedBy: "client",
      status: "approved",
      scheduleId: effectiveScheduleId || null,
    };

    setPayments(ps => [...ps, payment]);
    await applyApprovedPayment({
      payment,
      invoice: resolvedInvoice,
      sb,
      setInvoices,
      setPayments,
      setClients,
    });

    const teamIds = [...new Set([client?.assignedTo, ...staffUserIds(users)].filter(Boolean))];
    if (teamIds.length) {
      notify(teamIds, {
        type: "payment",
        title: "Payment received",
        body: `${INR(paidAmt)} received via Razorpay for ${invoice.invoiceNo} (ref ${reference}).`,
        meta: { invoiceId, paymentId, modal: "view-invoice" },
      });
    }
    await notifyCompanyUsers(invoice.clientId, { userLinks, users, clients }, {
      type: "payment",
      title: "Payment successful",
      body: `${INR(paidAmt)} paid for invoice ${invoice.invoiceNo}. Thank you!`,
      meta: { invoiceId, paymentId, modal: "view-invoice" },
    });
  };

  const payWithRazorpay = async () => {
    const paidAmt = outstanding;
    if (!paidAmt || paidAmt <= 0) { showToast("Nothing due on this invoice", "error"); return; }

    setStep("submitting");
    try {
      const { rzpResponse, order } = await runRazorpayPaymentLive({
        invoice,
        amount: paidAmt,
        customer,
        method: rzpMethod,
        orgName: org?.name || "Founders Bridge",
      });

      await recordRazorpayPayment(rzpResponse, order);
      setStep("success");
      setTimeout(() => {
        showToast(`${INR(paidAmt)} paid successfully`, "success");
        onClose();
      }, 1600);
    } catch (e) {
      const msg = e?.message || String(e);
      if (msg === "Payment cancelled") {
        setStep("form");
        return;
      }
      setErrMsg(msg);
      setStep("failed");
    }
  };

  const submitManual = async () => {
    const paidAmt = outstanding;
    if (!paidAmt || paidAmt <= 0) { showToast("Nothing due on this invoice", "error"); return; }
    if (!String(form.reference || "").trim()) {
      showToast("Enter UTR / transaction reference so our team can verify", "error");
      return;
    }

    setStep("submitting");
    try {
      const paymentId = uuid();
      const reference = String(form.reference).trim();
      const notes = form.notes?.trim()
        ? form.notes.trim()
        : "Client payment submitted — awaiting team approval";

      await sb.insert("payments", {
        id: paymentId,
        invoice_id: invoiceId,
        company_id: invoice.clientId,
        client_name: invoice.clientName,
        amount: paidAmt,
        mode: form.mode,
        reference,
        date: form.date || today(),
        notes,
        status: "pending_approval",
        schedule_id: effectiveScheduleId || null,
      });

      if (effectiveScheduleId) {
        await sb.update("payment_schedule", "id", effectiveScheduleId, { status: "pending_review" });
      }

      const pendingStatus = invoice.status === "paid" ? "paid" : "pending_approval";
      await sb.update("invoices", "id", invoiceId, { status: pendingStatus });

      setInvoices(is => is.map(i => (i.id === invoiceId ? { ...i, status: pendingStatus } : i)));
      setPayments(ps => [...ps, {
        id: paymentId,
        invoiceId,
        clientId: invoice.clientId,
        clientName: invoice.clientName,
        amount: paidAmt,
        mode: form.mode,
        reference,
        date: form.date || today(),
        notes,
        recordedBy: "client",
        status: "pending_approval",
        scheduleId: effectiveScheduleId || null,
      }]);

      const teamIds = [...new Set([client?.assignedTo, ...staffUserIds(users)].filter(Boolean))];
      if (teamIds.length) {
        notify(teamIds, {
          type: "payment",
          title: "Payment awaiting approval",
          body: `${INR(paidAmt)} submitted for ${invoice.invoiceNo} (${MANUAL_MODES.find(m => m.id === form.mode)?.label || form.mode}, ref ${reference}). Please review and approve.`,
          meta: { invoiceId, paymentId, modal: "view-invoice" },
        });
      }
      await notifyCompanyUsers(invoice.clientId, { userLinks, users, clients }, {
        type: "payment",
        title: "Payment submitted",
        body: `${INR(paidAmt)} submitted for invoice ${invoice.invoiceNo}. Our team will confirm once verified.`,
        meta: { invoiceId, paymentId, modal: "view-invoice" },
      });

      setStep("success");
      setTimeout(() => {
        showToast(`${INR(paidAmt)} submitted — awaiting team approval`, "success");
        onClose();
      }, 1600);
    } catch (e) {
      setErrMsg(e?.message || String(e));
      setStep("failed");
    }
  };

  const amountCard = (
    <div style={{ background: "linear-gradient(135deg,#0F172A,#1E40AF)", borderRadius: 12, padding: "18px 20px", marginBottom: 18, color: "#fff" }}>
      <div style={{ fontSize: 11, opacity: .7, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 6 }}>
        {payable.hasInstallments && !instAmount ? "Next instalment due" : "Amount Due"}
      </div>
      <div style={{ fontFamily: "'Fraunces',serif", fontSize: 34 }}>{INR(outstanding)}</div>
      <div style={{ fontSize: 11, opacity: .7, marginTop: 4 }}>{invoice.invoiceNo} · {invoice.clientName}</div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 460 }}>
        {step === "form" && (
          <>
            <div className="modal-head">
              <div className="modal-title">Pay Now</div>
              <button className="modal-close" type="button" onClick={onClose}><Icon name="close" size={16} /></button>
            </div>
            <div className="modal-body">
              {amountCard}

              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                {[
                  ["razorpay", "Pay online"],
                  ["manual", "Paid offline"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    className={`btn btn-sm${flow === id ? " btn-primary" : ""}`}
                    style={{ flex: 1 }}
                    onClick={() => setFlow(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {flow === "razorpay" && (
                <>
                  <div style={{ padding: "10px 12px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, fontSize: 12, color: "#1E40AF", marginBottom: 16 }}>
                    Pay securely via Razorpay. Your invoice updates automatically once payment succeeds.
                  </div>
                  <div className="f-group">
                    <label className="f-label">Payment method</label>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
                      {RZP_MODES.map(m => (
                        <button
                          key={m.id}
                          type="button"
                          className={`btn btn-sm${rzpMethod === m.id ? " btn-primary" : ""}`}
                          onClick={() => setRzpMethod(m.id)}
                        >
                          <InlineIcon name={m.icon} size={13} gap={5}>{m.label}</InlineIcon>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {flow === "manual" && (
                <>
                  <div style={{ padding: "10px 12px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, fontSize: 12, color: "#92400E", marginBottom: 16 }}>
                    Pay via your bank/UPI app first, then enter the details below. Our team will verify and approve.
                  </div>
                  <div className="form-grid">
                    <div className="f-group">
                      <label className="f-label">Payment mode</label>
                      <select className="f-select" value={form.mode} onChange={e => set("mode", e.target.value)}>
                        {MANUAL_MODES.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                      </select>
                    </div>
                    <div className="f-group">
                      <label className="f-label">UTR / Reference <span className="f-req">*</span></label>
                      <input className="f-input" placeholder="Transaction ID from bank/UPI" value={form.reference} onChange={e => set("reference", e.target.value)} />
                    </div>
                    <div className="f-group">
                      <label className="f-label">Payment date</label>
                      <input className="f-input" type="date" value={form.date} onChange={e => set("date", e.target.value)} />
                    </div>
                    <div className="f-group">
                      <label className="f-label">Notes (optional)</label>
                      <textarea className="f-textarea" rows={2} placeholder="Bank name, payer name, etc." value={form.notes} onChange={e => set("notes", e.target.value)} />
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="modal-foot">
              <button className="btn" type="button" onClick={onClose}>Cancel</button>
              {flow === "razorpay" ? (
                <button className="btn btn-green" type="button" onClick={payWithRazorpay}>
                  <InlineIcon name="card" size={14} gap={6}>Pay {INR(outstanding)}</InlineIcon>
                </button>
              ) : (
                <button className="btn btn-green" type="button" onClick={submitManual}>
                  <InlineIcon name="check" size={14} gap={6}>Submit for Approval</InlineIcon>
                </button>
              )}
            </div>
          </>
        )}
        {step === "submitting" && (
          <div style={{ padding: "48px 28px", textAlign: "center" }}>
            <div style={{ width: 44, height: 44, border: "3px solid #E5E4DF", borderTopColor: "var(--blue)", borderRadius: "50%", animation: "spin .8s linear infinite", margin: "0 auto 20px" }} />
            <div style={{ fontFamily: "'Fraunces',serif", fontSize: 20, marginBottom: 8 }}>
              {flow === "razorpay" ? "Opening Razorpay…" : "Submitting…"}
            </div>
          </div>
        )}
        {step === "success" && (
          <div style={{ padding: "48px 28px", textAlign: "center" }}>
            <div style={{ marginBottom: 16, color: "var(--green)" }}><Icon name="success" size={52} /></div>
            <div style={{ fontFamily: "'Fraunces',serif", fontSize: 22, marginBottom: 8 }}>
              {flow === "razorpay" ? "Payment Successful" : "Submitted for Approval"}
            </div>
            <div style={{ fontSize: 13, color: "var(--muted)" }}>
              {flow === "razorpay" ? "Your invoice has been updated." : "We will confirm once your payment is verified."}
            </div>
          </div>
        )}
        {step === "failed" && (
          <div style={{ padding: "48px 28px", textAlign: "center" }}>
            <div style={{ marginBottom: 16, color: "var(--red)" }}><Icon name="cancel" size={52} /></div>
            <div style={{ fontFamily: "'Fraunces',serif", fontSize: 22, marginBottom: 8 }}>Payment failed</div>
            <div style={{ fontSize: 12, color: "var(--red)", marginBottom: 20 }}>{errMsg || "Please try again."}</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button className="btn" type="button" onClick={onClose}>Close</button>
              <button className="btn btn-green" type="button" onClick={() => setStep("form")}>Try Again</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export { PayNowModal };
